import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import Icons from '../components/Icons';
import { groupItemsByDate } from '../utils/grouping';
import toast from 'react-hot-toast';
import * as api from '../services/api';

export default function ProfilePage() {
  const { user, token, logout } = useAuth();
  const { credits, plan, profile, items, payHistory, refreshPayHistory, refreshProfile } = useCredits();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Trigger a fetch when the page mounts — no-op if cache is still warm
  useEffect(() => {
    refreshPayHistory();
  }, [refreshPayHistory]);

  const avatarUrl = user?.photoURL || `https://api.dicebear.com/7.x/shapes/svg?seed=${user?.uid}`;
  const displayName = user?.name || user?.email?.split('@')[0] || 'User';
  const totalImages = profile?.stats?.images || items.filter(i => i.type !== 'video').length;
  const totalVideos = profile?.stats?.videos || items.filter(i => i.type === 'video').length;
  const creditsUsed = profile?.stats?.creditsUsed || 0;
  const totalCreations = profile?.stats?.total || items.length;
  const cardSubscription = profile?.cardSubscription || null;
  const isCardSubscription = cardSubscription?.provider === 'lemonsqueezy';
  const cardRenewalCancelled = isCardSubscription && (
    cardSubscription.cancelled ||
    cardSubscription.status === 'cancelled' ||
    cardSubscription.autoRenew === false
  );
  const cardAccessEndsAt = cardSubscription?.accessEndsAt || cardSubscription?.endsAt || profile?.planExpiry || null;
  const cardRenewsAt = cardSubscription?.renewsAt || cardAccessEndsAt;
  const formatDate = (value) => {
    if (!value) return 'the end of your paid period';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'the end of your paid period';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleCancelCardSubscription = async () => {
    if (!token || cancelLoading) return;
    setCancelLoading(true);
    try {
      const result = await api.cancelLemonSqueezySubscription(token);
      await refreshProfile(true);
      await refreshPayHistory(true);
      setShowCancelConfirm(false);
      toast.success(result.alreadyCancelled ? 'Card renewal was already cancelled.' : 'Card renewal cancelled. You keep access until the paid period ends.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel card renewal.');
    } finally {
      setCancelLoading(false);
    }
  };



  return (
    <>
      <div className="main">
        <div className="mtbar">
          <div className="mtab on">Profile</div>
        </div>
        <div className="mbody">
          <div className="profile-page">
            {/* Header */}
            <div className="profile-header">
              <img src={avatarUrl} alt="" className="pf-av" />
              <div className="profile-name">{displayName}</div>
              <div className="profile-email">{user?.email}</div>
              <div className="profile-badge">
                <Icons.Crown sz={11} /> {plan} plan
              </div>
            </div>

            {/* Stats Grid */}
            <div className="profile-stats">
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--gr)' }}>{credits}</div>
                <div className="stat-label">Credits</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totalCreations}</div>
                <div className="stat-label">Total Creations</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totalImages}</div>
                <div className="stat-label">Images</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totalVideos}</div>
                <div className="stat-label">Videos</div>
              </div>
            </div>

            {/* Details */}
            <div style={{ background: 'var(--s1)', borderRadius: 'var(--r2)', border: '1px solid var(--b1)', padding: 20, marginBottom: 20 }}>
              <div className="slbl" style={{ marginBottom: 12 }}>Account Details</div>
              {[
                ['Email', user?.email],
                ['Plan', plan?.toUpperCase()],
                ['Credits Remaining', credits],
                ['Credits Used', creditsUsed],
                ['Plan Expiry', profile?.planExpiry ? new Date(profile.planExpiry).toLocaleDateString() : 'N/A'],
                ['Member Since', user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'N/A'],
              ].map(([label, value]) => (
                <div key={label} className="pf-stat">
                  <span className="pf-sl">{label}</span>
                  <span className="pf-sv">{value}</span>
                </div>
              ))}
            </div>

            {/* Card Subscription */}
            <div style={{ background: 'var(--s1)', borderRadius: 'var(--r2)', border: '1px solid var(--b1)', padding: 20, marginBottom: 20 }}>
              <div className="slbl" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Crown sz={13} /> Card Subscription
              </div>
              {isCardSubscription ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ color: 'var(--t2)', fontSize: 13, lineHeight: 1.6 }}>
                    {cardRenewalCancelled ? (
                      <>Renewal is cancelled. You will not be charged again, and your current {String(cardSubscription.plan || plan || 'Go').toUpperCase()} plan stays active until {formatDate(cardAccessEndsAt)}.</>
                    ) : (
                      <>Your card subscription renews automatically on {formatDate(cardRenewsAt)}. Cancel renewal anytime; your current {String(cardSubscription.plan || plan || 'Go').toUpperCase()} plan stays active until the paid period ends.</>
                    )}
                  </div>
                  {!cardRenewalCancelled && (
                    <button
                      className="gbtn"
                      onClick={() => setShowCancelConfirm(true)}
                      disabled={cancelLoading}
                      style={{
                        alignSelf: 'flex-start',
                        background: 'rgba(239,68,68,0.08)',
                        color: '#ef4444',
                        border: '1px solid rgba(239,68,68,0.25)',
                        boxShadow: 'none',
                      }}
                    >
                      <Icons.X sz={14} /> Cancel renewal
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ color: 'var(--t3)', fontSize: 13, lineHeight: 1.6 }}>
                  No auto-renewing card subscription found. bKash/manual plans do not auto-renew and do not need cancellation.
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="gbtn" style={{ flex: 1 }} onClick={() => navigate('/subscription')}>
                <Icons.Crown sz={14} /> Upgrade Plan
              </button>
              <button className="gbtn" style={{ flex: 1, background: 'linear-gradient(135deg, #10b981, #059669)' }}
                onClick={() => navigate('/gallery')}>
                <Icons.Grid sz={14} /> My Creations
              </button>
            </div>
            
            <div style={{ display: 'flex', marginTop: 10, marginBottom: 20, gap: 10 }}>
              <button className="gbtn" style={{ flex: 1, background: 'var(--s2)', color: 'var(--t1)', border: '1px solid var(--b2)' }}
                onClick={() => navigate('/edit-profile')}>
                <Icons.Edit sz={14} /> Edit Profile
              </button>
              <button className="gbtn" style={{ flex: 1, background: '#fee2e2', color: '#ef4444', border: '1px solid #fecaca' }}
                onClick={() => setShowLogoutConfirm(true)}>
                <Icons.Logout sz={14} /> Sign out
              </button>
            </div>

            {/* Payment History */}
            <div style={{ background: 'var(--s1)', borderRadius: 'var(--r2)', border: '1px solid var(--b1)', padding: 20, marginBottom: 20 }}>
              <div className="slbl" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                💳 Payment History
              </div>
              {payHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--t3)', fontSize: 12 }}>
                  No payments yet
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {payHistory.map(p => {
                    const isPlan = p.type === 'subscription' || p.type === 'plan';
                    const planLabel = isPlan
                      ? (p.plan ? `${p.plan.charAt(0).toUpperCase() + p.plan.slice(1)} Plan` : 'Plan')
                      : 'Credit Pack';
                    const cardBg    = isPlan ? 'rgba(109,40,217,0.06)' : 'rgba(16,185,129,0.04)';
                    const cardBorder= isPlan ? '1px solid rgba(109,40,217,0.2)' : '1px solid rgba(16,185,129,0.15)';
                    const iconEmoji = isPlan ? '👑' : '⚡';
                    const labelColor= isPlan ? 'var(--ac3)' : 'var(--gr)';

                    return (
                      <div key={p.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 14px', background: cardBg, borderRadius: 12,
                        border: cardBorder, fontSize: 12, gap: 10,
                      }}>
                        {/* Icon */}
                        <div style={{
                          width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                          background: isPlan ? 'rgba(109,40,217,0.12)' : 'rgba(16,185,129,0.10)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                        }}>
                          {iconEmoji}
                        </div>

                        {/* Main info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, color: labelColor, fontSize: 13, marginBottom: 2 }}>
                            {planLabel}
                          </div>
                          <div style={{ color: 'var(--t3)', fontSize: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span>{p.method === 'paddle' || p.method === 'lemonsqueezy' ? '💳 Card' : p.method === 'bkash' ? '🇧🇩 bKash' : p.method}</span>
                            <span style={{ opacity: 0.4 }}>·</span>
                            <span>{p.date ? new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                          </div>
                        </div>

                        {/* Credits + amount */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          <div style={{ fontWeight: 800, color: 'var(--gr)', fontSize: 13 }}>
                            +{(p.credits || 0).toLocaleString()} cr
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--t3)' }}>
                            {p.amount > 0 ? `${p.currency === 'BDT' ? '৳' : '$'}${p.amount}` : '—'}
                          </div>
                        </div>

                        {/* Status badge */}
                        <div style={{
                          padding: '3px 9px', borderRadius: 99, fontSize: 9, fontWeight: 700, flexShrink: 0,
                          background: p.status === 'completed'
                            ? 'rgba(16,185,129,0.15)'
                            : p.status === 'pending'
                              ? 'rgba(245,158,11,0.15)'
                              : 'rgba(239,68,68,0.12)',
                          color: p.status === 'completed' ? '#10b981'
                            : p.status === 'pending' ? '#f59e0b' : '#ef4444',
                          marginLeft: 4,
                        }}>
                          {p.status}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>


          </div>
        </div>
      </div>

      {/* Logout Confirmation Popup */}
      {showLogoutConfirm && (
        <div className="mp-overlay" onClick={() => setShowLogoutConfirm(false)} style={{ zIndex: 80 }}>
          <div className="logout-confirm" onClick={e => e.stopPropagation()}>
            <div className="logout-icon">👋</div>
            <div className="logout-title">Sign out?</div>
            <div className="logout-desc">Are you sure you want to sign out of aiField?</div>
            <div className="logout-btns">
              <button className="logout-btn cancel" onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </button>
              <button className="logout-btn confirm" onClick={async () => {
                setShowLogoutConfirm(false);
                await logout();
              }}>
                <Icons.Logout sz={13} /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Card Renewal Popup */}
      {showCancelConfirm && (
        <div className="mp-overlay" onClick={() => !cancelLoading && setShowCancelConfirm(false)} style={{ zIndex: 80 }}>
          <div className="logout-confirm" onClick={e => e.stopPropagation()} style={{ borderColor: 'rgba(239,68,68,0.25)', maxWidth: 390 }}>
            <div className="logout-icon"><Icons.X sz={24} /></div>
            <div className="logout-title">Cancel card renewal?</div>
            <div className="logout-desc">
              This stops future card charges. Your current {String(cardSubscription?.plan || plan || 'Go').toUpperCase()} plan stays active until {formatDate(cardAccessEndsAt)}.
            </div>
            <div className="logout-btns">
              <button className="logout-btn cancel" onClick={() => setShowCancelConfirm(false)} disabled={cancelLoading}>
                Keep renewal
              </button>
              <button className="logout-btn confirm" onClick={handleCancelCardSubscription} disabled={cancelLoading}>
                <Icons.X sz={13} /> {cancelLoading ? 'Cancelling...' : 'Cancel renewal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
